"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { useShallow } from "zustand/react/shallow";
import { useCreatePageStore } from "@/stores/createPageStore";
import { AgentActionPayload, PresetOption } from "@/types/createAgent";
import type { DesignerChoice } from "@/lib/designer/contracts";
import { CreateFormData } from "@/components/create/types";
import { logger } from "@/utils/logger";
import { trackEvent } from "@/utils/analytics";
import { isCreditPresetAction } from "@/lib/agent/presetPresentation";
import {
  evaluateAgentActionReadiness,
  hasConfirmedBackDesignDirection,
} from "@/lib/agent/actionReadinessGuards";
import {
  getRendererDirectMessagePresets,
  getRendererDirectWidgetPresetsForSurface,
} from "@/lib/agent/visiblePresetCapabilities";
import { requestVirtualTryOn } from "@/lib/create/virtualTryOnEvents";
import {
  createDesignSpecSnapshot,
  diffDesignSpecFields,
} from "@/lib/agent/designSpecSnapshot";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import { getGeneratedFrontDesignSpec } from "@/lib/designer/savedDesign";
import { restoreDesignDraft } from "@/lib/designer/restoreDesignDraft";
import { useAuth } from "@/context/providers/AuthProvider";
import { useSafeCreditsContext } from "@/context/providers/CreditsProvider";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { prepareSavePointPayload } from "@/hooks/create-page/utils/formStateHelpers";
import {
  buildLocalBackChoiceSelectionPreset,
  buildGenerateFooterPreset,
  withFrontApprovalForBackChoice,
} from "@/lib/agent/footerActions";
import {
  buildActionPayload,
  buildExplorePreset,
  shouldTriggerUpload,
} from "../createAgentChat.utils";
import type { ChatActions } from "../ChatActionsProvider";

/** Dev/harness escape hatch: prompt-inspection mode never creates an image. */
function isGeneratePromptInspectEnabled(searchParams: {
  get: (name: string) => string | null;
}): boolean {
  return searchParams.get("inspectGeneratePrompt") === "1";
}

function isSameRenderedTryOnPreset(
  candidate: PresetOption,
  clicked: PresetOption
): boolean {
  return (
    candidate.action === "action" &&
    candidate.actionId === "start_virtual_try_on" &&
    candidate.action === clicked.action &&
    candidate.actionId === clicked.actionId &&
    candidate.label === clicked.label
  );
}

/**
 * Local try-on is free and repeatable, but it still originates from a current
 * server-authored capability. Verify the exact live message/widget source and
 * the same shared front-image readiness rule before bypassing a chat turn.
 */
function isTrustedRenderedTryOnClick(
  preset: PresetOption,
  source: AgentActionPayload["source"] | undefined
): boolean {
  if (
    preset.action !== "action" ||
    preset.actionId !== "start_virtual_try_on" ||
    !source?.messageId
  ) {
    return false;
  }

  const state = useCreatePageStore.getState();
  const latestAssistant = [...state.agent.conversation.messages]
    .reverse()
    .find(message => message.role === "assistant");
  if (!latestAssistant || latestAssistant.id !== source.messageId) {
    return false;
  }

  const widgets = latestAssistant.widgets ?? [];
  let sourcePresets: PresetOption[];
  if (source.widgetId) {
    if (typeof source.widgetVersion !== "number") return false;
    const sourceWidget = widgets.find(
      widget =>
        widget.id === source.widgetId &&
        widget.version === source.widgetVersion &&
        widget.active === true
    );
    if (!sourceWidget) return false;
    sourcePresets = getRendererDirectWidgetPresetsForSurface(
      sourceWidget,
      widgets
    );
  } else {
    // A widget-backed control must carry its widget coordinates. Message-only
    // provenance is reserved for the legacy server-authored preset surface.
    if (source.widgetVersion !== undefined || widgets.length > 0) return false;
    sourcePresets = getRendererDirectMessagePresets(
      latestAssistant.presets ?? []
    );
  }

  if (
    !sourcePresets.some(candidate =>
      isSameRenderedTryOnPreset(candidate, preset)
    )
  ) {
    return false;
  }

  return evaluateAgentActionReadiness({
    actionId: preset.actionId,
    phase: state.agent.phase,
    formState: state.form.data,
    hasFrontImage: Boolean(state.generation.images.front.url),
    hasBackImage: Boolean(state.generation.images.back.url),
    frontApproved: state.form.data.frontApproved === true,
  }).allowed;
}

interface UsePresetDispatchParams {
  selectionScope?: string;
  sendAgentMessage: ChatActions["sendAgentMessage"];
  generateImages: ChatActions["generateImages"];
  handleCreateTicket: ChatActions["handleCreateTicket"];
  /** Delegates upload-type presets to useChatUpload's picker flow. */
  startPresetUpload: (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  /** Opens the login-required dialog for anonymous credit actions. */
  onRequireLogin: () => void;
}

/**
 * Preset dispatch state + handlers: tracks the processing preset and the
 * selected label per message, and routes preset clicks/selections to the
 * agent (suggest/action/modify), gating account and credit requirements before
 * they consume the current server-authored action surface.
 */
export function usePresetDispatch({
  selectionScope,
  sendAgentMessage,
  generateImages,
  handleCreateTicket,
  startPresetUpload,
  onRequireLogin,
}: UsePresetDispatchParams) {
  const { user, isAnonymous, roles, loading: authLoading } = useAuth();
  const {
    balance: creditBalance,
    isLoading: creditsLoading,
    isFetching: creditsFetching,
  } = useSafeCreditsContext();
  const searchParams = useSearchParams();
  const agentPhase = useCreatePageStore(state => state.agent.phase);
  const addMessage = useCreatePageStore(state => state.addMessage);
  const showCreditModal = useCreatePageStore(state => state.setShowCreditModal);
  const setShowStartOverConfirm = useCreatePageStore(
    state => state.setShowStartOverConfirm
  );
  const updateFormData = useCreatePageStore(state => state.updateFormData);
  const stageBackChoice = useCreatePageStore(state => state.stageBackChoice);
  const setCurrentView = useCreatePageStore(state => state.setCurrentView);
  const loading = useCreatePageStore(
    state => state.generation.progress.loading
  );
  const isModifying = useCreatePageStore(
    state => state.ui.modification.isModifying
  );
  const imageUrl = useCreatePageStore(
    useShallow(state => state.generation.images.front.url)
  );
  const [processingPreset, setProcessingPreset] = useState<{
    scope: typeof selectionScope;
    id: string;
  } | null>(null);
  const processingPresetId =
    processingPreset && processingPreset.scope === selectionScope
      ? processingPreset.id
      : null;
  const setProcessingPresetId = (id: string | null) => {
    setProcessingPreset(current =>
      id
        ? { scope: selectionScope, id }
        : current?.scope === selectionScope
          ? null
          : current
    );
  };
  const [selectedPresetLabelsByMessageId, setSelectedPresetLabelsByMessageId] =
    useState<Record<string, string>>({});

  const [selectedChoicesByMessageId, setSelectedChoicesByMessageId] = useState<
    Record<string, DesignerChoice>
  >({});

  const [lastSelectionScope, setLastSelectionScope] = useState(selectionScope);
  if (lastSelectionScope !== selectionScope) {
    setLastSelectionScope(selectionScope);
    setSelectedPresetLabelsByMessageId({});
    setSelectedChoicesByMessageId({});
  }

  const setSelectedPresetLabel = (messageId: string, label: string) => {
    setSelectedPresetLabelsByMessageId(current => ({
      ...current,
      [messageId]: label,
    }));
  };

  const clearSelectedPresetLabel = (messageId: string) => {
    setSelectedChoicesByMessageId(current => {
      const next = { ...current };
      delete next[messageId];
      return next;
    });
    setSelectedPresetLabelsByMessageId(current => {
      const next = { ...current };
      delete next[messageId];
      return next;
    });
  };

  const sendPresetAction = async (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => {
    // Buttons select or act — they never speak for the user. The label still
    // travels to the agent as turn content (and history), but no user bubble
    // is rendered; the compact selection receipt is the transcript record
    // once a choice is complete (documentation/CreateChatActionStyle.md).
    await sendAgentMessage(preset.label, undefined, undefined, {
      displayMessage: preset.label,
      hideUserMessage: true,
      actionPayload: buildActionPayload(preset, source),
    });
  };

  const getCurrentDraftDesignSpec = () => {
    const currentState = useCreatePageStore.getState();
    return createDesignSpecSnapshot(
      compileCurrentDesignSpec(
        currentState.agent.designBrief,
        currentState.form.data,
        {
          hasFrontImage: Boolean(currentState.generation.images.front.url),
          hasBackImage: Boolean(currentState.generation.images.back.url),
        }
      )
    );
  };

  /**
   * A server-authored preset is an immutable capability. When the editable
   * draft still matches its authored DesignSpec, keep the normal signed chat
   * path. When details changed (or a legacy action has no comparable spec),
   * render through the page's existing authenticated action with the latest
   * frozen draft instead of forging a different actionPayload.
   */
  const runGeneratePreset = async (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => {
    const draftDesignSpec = getCurrentDraftDesignSpec();
    const authoredDesignSpec = preset.actionData?.designSpec;
    const draftDiffersFromAuthored = Boolean(
      draftDesignSpec &&
      (!authoredDesignSpec ||
        diffDesignSpecFields(authoredDesignSpec, draftDesignSpec).length > 0)
    );

    if (draftDiffersFromAuthored) {
      await generateImages(
        preset.actionId === "generate_dress_back" ? 4 : 2,
        true,
        draftDesignSpec ?? undefined
      );
      return;
    }

    await sendPresetAction(preset, source);
  };

  const getCurrentQuoteDesignSpec = (preset?: PresetOption) =>
    getCurrentDraftDesignSpec() ?? preset?.actionData?.designSpec;

  const resolveQuoteSelection = (preset: PresetOption) => {
    const currentState = useCreatePageStore.getState();
    const frontOnly = preset.actionData?.quoteScope === "front_only";
    const designSpec = frontOnly
      ? getGeneratedFrontDesignSpec(currentState.generation.images.front)
      : getCurrentQuoteDesignSpec(preset);
    if (frontOnly && !designSpec) {
      addMessage({
        role: "assistant",
        content:
          "I can't verify the saved front preview for a front-only quote. Create a current front preview first.",
      });
      return { allowed: false, designSpec: undefined };
    }
    const readiness = evaluateAgentActionReadiness({
      actionId: "submit_quote",
      phase: frontOnly ? "front_review" : currentState.agent.phase,
      formState: frontOnly
        ? restoreDesignDraft(designSpec!).formData
        : currentState.form.data,
      hasFrontImage: Boolean(currentState.generation.images.front.url),
      hasBackImage:
        !frontOnly && Boolean(currentState.generation.images.back.url),
      frontApproved: currentState.form.data.frontApproved === true,
      hasBackDesignDirection: hasConfirmedBackDesignDirection(designSpec),
    });
    if (!readiness.allowed) {
      addMessage({ role: "assistant", content: readiness.message });
    }
    return {
      allowed: readiness.allowed,
      designSpec: designSpec ?? undefined,
      quoteScope: frontOnly ? ("front_only" as const) : undefined,
    };
  };

  const updateFormDataFromPreset = (preset: PresetOption) => {
    if (preset.data) {
      updateFormData(preset.data as Partial<CreateFormData>);
    }
  };

  const persistCurrentSelection = () => {
    const currentState = useCreatePageStore.getState();
    FormPersistenceService.saveState({
      data: prepareSavePointPayload(currentState, 1),
      source: "auto_save",
    });
  };

  const markWidgetSelection = useCreatePageStore(
    state => state.markWidgetSelection
  );

  const savePresetSelection = (
    messageId: string,
    preset: PresetOption,
    choice?: DesignerChoice
  ) => {
    updateFormDataFromPreset(preset);
    setSelectedPresetLabel(messageId, preset.label);
    // Also stamp the pick onto the message's widgets: widgets round-trip
    // through the persisted history, so the receipt survives reloads (the
    // in-memory label map and the hidden commit turn — which says "Generate",
    // not the pick — do not).
    markWidgetSelection(messageId, preset.label, choice);
    // The save point is a synchronous snapshot. Persist after stamping the
    // widget, or a reload loses the chosen option and its Back CTA.
    persistCurrentSelection();
  };

  const trackLoginWallShown = (source: string) => {
    trackEvent("create_login_wall_shown", {
      journey_stage: "create",
      source,
      phase: agentPhase,
    });
  };

  const handlePresetSelect = (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => {
    if (useCreatePageStore.getState().ticket.designSubmitted) return;
    if (shouldTriggerUpload(preset)) {
      startPresetUpload(messageId, preset, source);
      return;
    }

    setSelectedChoicesByMessageId(current => ({
      ...current,
      [messageId]: { preset, source: { ...source, messageId } },
    }));
    const isFrontReviewBackChoice =
      agentPhase === "front_review" &&
      Boolean(imageUrl) &&
      (preset.actionId === "set_back_prompt" ||
        preset.actionId === "set_back_style");
    const backChoiceData = (preset.data ?? {}) as Record<string, unknown>;
    const selectedBackPrompt = [
      backChoiceData.backPrompt,
      backChoiceData.backStyle,
    ]
      .filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0
      )[0]
      ?.trim();
    if (isFrontReviewBackChoice && selectedBackPrompt) {
      // A picker selection is a staged design detail, not permission to spend
      // a credit. Write it into the canonical brief so preview planning sees
      // this exact choice even when the earlier brief named another back.
      // Workflow flags remain uncommitted until the explicit Generate click.
      stageBackChoice(selectedBackPrompt);
    }
    savePresetSelection(
      messageId,
      isFrontReviewBackChoice
        ? buildLocalBackChoiceSelectionPreset(preset)
        : preset,
      { preset, source: { ...source, messageId } }
    );
  };

  const setViewForModifyAction = (actionId: string) => {
    if (actionId === "modify_dress_front") {
      setCurrentView("front");
    } else if (actionId === "modify_dress_back") {
      setCurrentView("back");
    }
  };

  /**
   * Gate credit-consuming actions before creating a chat turn. Supabase gives
   * guests an anonymous user object, so `!user` alone does not identify the
   * guest journey. Every render or modification opens the account/credit
   * handoff before consuming the current action surface.
   */
  const creditModalContextForPreset = (
    preset: PresetOption
  ): "generate_front" | "generate_back" | "modify" => {
    if (
      preset.actionId === "modify_dress_front" ||
      preset.actionId === "modify_dress_back"
    ) {
      return "modify";
    }
    return preset.actionId === "generate_dress_back"
      ? "generate_back"
      : "generate_front";
  };

  const gateCreditAction = (preset: PresetOption): boolean => {
    if (
      !isCreditPresetAction(preset.actionId) ||
      authLoading ||
      isGeneratePromptInspectEnabled(searchParams)
    ) {
      return false;
    }

    if (!user) {
      trackLoginWallShown("credit_preset");
      onRequireLogin();
      return true;
    }

    if (isAnonymous === true) {
      trackLoginWallShown("credit_preset_anonymous");
      showCreditModal("anon_unlock");
      return true;
    }

    // Do not create a hidden agent turn just to discover an already-loaded
    // zero balance. That turn consumes the current Generate/Apply capability
    // and replaces it with a top-up-only response, so checkout returns to a
    // dead end. Opening the local credit handoff preserves the exact current
    // action surface; once the refreshed balance is positive, the same button
    // remains ready for the customer's explicit (credit-spending) click.
    // Unknown/loading balances deliberately fall through to the server rather
    // than treating a paying customer as empty.
    if (
      roles?.admin !== true &&
      creditBalance === 0 &&
      !creditsLoading &&
      !creditsFetching
    ) {
      showCreditModal(creditModalContextForPreset(preset));
      return true;
    }

    return false;
  };

  const gateAccountRequiredQuote = (preset: PresetOption): boolean => {
    if (preset.actionId !== "submit_quote" || authLoading) return false;
    if (isAnonymous === true) {
      trackLoginWallShown("quote_preset_anonymous");
      // Let the ticket action store the selected render contract and scope
      // before it opens the account dialog. Opening it here loses that choice
      // when the customer completes authentication.
      return false;
    }
    if (!user) {
      trackLoginWallShown("quote_preset");
      // The ticket action persists the exact selected contract before it
      // redirects to sign-in. A generic login dialog would lose this scope.
      return false;
    }
    return false;
  };

  const openBuyCredits = (
    context: "generate_front" | "generate_back" | "modify"
  ) => {
    if (!user) {
      trackLoginWallShown("buy_credits");
      onRequireLogin();
      return;
    }
    if (isAnonymous === true) {
      trackLoginWallShown("buy_credits_anonymous");
      showCreditModal("anon_unlock");
      return;
    }
    showCreditModal(context);
  };

  const handleGenerateFromSelectedPreset = async (
    preset: PresetOption,
    source?: AgentActionPayload["source"],
    onGenerationAccepted?: () => void,
    applyCommittedChanges?: () => Promise<void>
  ) => {
    if (
      useCreatePageStore.getState().ticket.designSubmitted ||
      processingPresetId ||
      loading ||
      isModifying
    ) {
      return;
    }

    const generatePreset = buildGenerateFooterPreset(preset, {
      currentPhase: agentPhase,
      hasFrontImage: Boolean(imageUrl),
    });
    if (gateCreditAction(generatePreset)) return;
    onGenerationAccepted?.();

    const presetId = `${preset.actionId}-${preset.label}`;
    setProcessingPresetId(presetId);

    try {
      if (!applyCommittedChanges) updateFormDataFromPreset(preset);

      if (applyCommittedChanges) {
        await applyCommittedChanges();
        return;
      }

      await runGeneratePreset(
        generatePreset,
        source?.messageId ? { messageId: source.messageId } : source
      );
    } catch (error) {
      logger.error("Failed to generate from selected preset", {
        component: "CreateAgentChat",
        metadata: {
          error: error instanceof Error ? error.message : String(error),
          presetId: preset.actionId,
        },
      });
      addMessage({
        role: "assistant",
        content: "We couldn't create your preview. Please try again.",
      });
    } finally {
      setTimeout(() => {
        setProcessingPresetId(null);
      }, 300);
    }
  };

  const handleSelectedPresetGenerate = async (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"],
    applyCommittedChanges?: () => Promise<void>
  ) => {
    const effectivePreset = withFrontApprovalForBackChoice(preset, {
      currentPhase: agentPhase,
      hasFrontImage: Boolean(imageUrl),
    });
    const acceptedPreset = applyCommittedChanges
      ? {
          ...effectivePreset,
          data: Object.fromEntries(
            Object.entries(effectivePreset.data ?? {}).filter(
              ([field]) => field === "frontApproved" || field === "generateBack"
            )
          ),
        }
      : effectivePreset;
    await handleGenerateFromSelectedPreset(
      acceptedPreset,
      source,
      () =>
        savePresetSelection(messageId, acceptedPreset, {
          preset,
          source: { ...source, messageId },
        }),
      applyCommittedChanges
    );
  };

  const handleSelectedPresetChatMore = async (
    messageId: string,
    preset: PresetOption | null,
    source?: AgentActionPayload["source"]
  ) => {
    if (useCreatePageStore.getState().ticket.designSubmitted) return;
    // No pick means the offered set missed. Carry no design data — there is no
    // chosen direction to develop — and flag the turn so the planner realigns
    // with what the customer actually wants instead of talking about an option
    // they declined. Not a synthesized user message: this stays an agent
    // action, so the transcript never shows words the customer did not type.
    if (!preset) {
      await handlePresetClick(
        {
          label: "None of these fit",
          action: "suggest",
          actionId: "custom",
          data: {},
          actionData: { exploreMore: true, noneSelected: true },
        },
        source?.messageId ? { messageId: source.messageId } : { messageId }
      );
      return;
    }
    // "Explore this direction" / "Chat More": tag the resume as an explore
    // intent (Fix D) so the server opens a CONVERSATION about the direction and
    // does not commit or advance the phase. Marked here rather than in
    // withFrontApprovalForBackChoice because only the Chat More path explores;
    // the Generate path commits.
    const explorePreset = buildExplorePreset(preset);
    // Exploring a back direction is still conversation, not approval. Only the
    // explicit Generate/continue path may set frontApproved or generateBack.
    savePresetSelection(messageId, explorePreset, {
      preset,
      source: { ...source, messageId },
    });
    await handlePresetClick(
      explorePreset,
      source?.messageId ? { messageId: source.messageId } : source
    );
  };

  /**
   * A restored ticket has no server-authored transcript surface to sign its
   * first set of controls. These two commits therefore use the page's existing
   * local actions instead of sending an unverifiable capability as chat prose.
   * Once the customer continues chatting, subsequent widgets are server-owned
   * and use the normal action-payload path above.
   */
  const handleRestoredQuote = async () => {
    const quotePreset: PresetOption = {
      label: "Get free quote",
      action: "action",
      actionId: "submit_quote",
      presetType: "commit_action",
      data: {},
      actionData: { shouldCreateTicket: true },
    };
    if (
      useCreatePageStore.getState().ticket.designSubmitted ||
      processingPresetId ||
      loading ||
      isModifying
    )
      return;
    const selection = resolveQuoteSelection(quotePreset);
    if (!selection.allowed) return;
    if (gateAccountRequiredQuote(quotePreset)) return;

    const presetId = `${quotePreset.actionId}-${quotePreset.label}`;
    setProcessingPresetId(presetId);
    try {
      await handleCreateTicket(selection.designSpec);
    } finally {
      setTimeout(() => setProcessingPresetId(null), 300);
    }
  };

  const handleRestoredPresetGenerate = async (
    messageId: string,
    preset: PresetOption,
    applyCommittedChanges?: () => Promise<void>
  ) => {
    if (
      useCreatePageStore.getState().ticket.designSubmitted ||
      processingPresetId ||
      loading ||
      isModifying
    )
      return;
    const effectivePreset = withFrontApprovalForBackChoice(preset, {
      currentPhase: agentPhase,
      hasFrontImage: Boolean(imageUrl),
    });
    const generatePreset = buildGenerateFooterPreset(effectivePreset, {
      currentPhase: agentPhase,
      hasFrontImage: Boolean(imageUrl),
    });
    if (gateCreditAction(generatePreset)) return;

    const presetId = `${generatePreset.actionId}-${generatePreset.label}`;
    setProcessingPresetId(presetId);
    try {
      const acceptedPreset = applyCommittedChanges
        ? {
            ...effectivePreset,
            data: Object.fromEntries(
              Object.entries(effectivePreset.data ?? {}).filter(
                ([field]) =>
                  field === "frontApproved" || field === "generateBack"
              )
            ),
          }
        : effectivePreset;
      savePresetSelection(messageId, acceptedPreset);
      if (!applyCommittedChanges) updateFormDataFromPreset(effectivePreset);
      if (applyCommittedChanges) {
        await applyCommittedChanges();
        return;
      }
      await generateImages(
        generatePreset.actionId === "generate_dress_back" ? 4 : 2,
        true,
        getCurrentDraftDesignSpec() ?? undefined
      );
    } finally {
      setTimeout(() => setProcessingPresetId(null), 300);
    }
  };

  /** Only called by the workspace after its exact live source checks pass. */
  const handleCommittedPresetApply = async (
    preset: PresetOption,
    applyCommittedChanges: () => Promise<void>
  ) => {
    const current = useCreatePageStore.getState();
    if (
      current.ticket.designSubmitted ||
      processingPresetId ||
      current.generation.progress.loading ||
      current.ui.modification.isModifying
    )
      return;
    if (gateCreditAction(preset)) return;
    setProcessingPresetId(`${preset.actionId}-${preset.label}`);
    try {
      // A direct render action may have been authored before a manual edit.
      // Its signed payload is unchanged; apply the latest committed draft.
      await applyCommittedChanges();
    } finally {
      setTimeout(() => setProcessingPresetId(null), 300);
    }
  };

  const handlePresetClick = async (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => {
    // Prevent multiple clicks while processing
    if (
      useCreatePageStore.getState().ticket.designSubmitted ||
      processingPresetId ||
      loading ||
      isModifying
    ) {
      return;
    }
    if (preset.actionId === "start_virtual_try_on") {
      if (!isTrustedRenderedTryOnClick(preset, source)) return;
      // Opening/closing the uploader is repeatable exploration. Do not append
      // a hidden turn, change phase, or consume/deactivate the review preset.
      trackEvent("create_virtual_try_on_requested", {
        journey_stage: "create",
        source: "agent_preset",
        phase: agentPhase,
      });
      requestVirtualTryOn();
      return;
    }
    if (gateCreditAction(preset)) {
      return;
    }
    const quoteSelection =
      preset.actionId === "submit_quote" ? resolveQuoteSelection(preset) : null;
    if (quoteSelection && !quoteSelection.allowed) return;
    if (gateAccountRequiredQuote(preset)) {
      return;
    }

    // Create unique ID for this preset to track processing state
    const presetId = `${preset.actionId}-${preset.label}`;
    setProcessingPresetId(presetId);

    try {
      switch (preset.action) {
        case "suggest":
          try {
            // A direct back choice in front review is the customer's approval
            // to move forward. Persist that workflow state locally, but send
            // the ORIGINAL server-authored preset: provenance validation
            // requires an exact match to the rendered capability. The server
            // independently derives the same transition from the trusted
            // action id; Explore/Chat More is excluded by the helper.
            const effectivePreset = withFrontApprovalForBackChoice(preset, {
              currentPhase: agentPhase,
              hasFrontImage: Boolean(imageUrl),
            });
            updateFormDataFromPreset(effectivePreset);
            await sendPresetAction(preset, source);
          } catch (error) {
            logger.error("Failed to process suggest preset", {
              component: "CreateAgentChat",
              metadata: {
                error: error instanceof Error ? error.message : String(error),
                presetId: preset.actionId,
              },
            });
            addMessage({
              role: "assistant",
              content: "We couldn't save your selection. Please try again.",
            });
          }
          break;
        case "action": {
          const { actionId } = preset;
          try {
            if (actionId === "start_over") {
              // Destructive reset — confirm before wiping the design, mirroring
              // the header Start Over button. The confirmation modal performs
              // the actual reset on approval.
              setShowStartOverConfirm(true);
              break;
            }
            if (actionId === "buy_credits_back") {
              // The credit wall can also refuse a FRONT render, which has no
              // top-up action id of its own; the agent stamps the refused side
              // so the modal names it instead of talking about the back view.
              openBuyCredits(
                preset.actionData?.generateType === "front"
                  ? "generate_front"
                  : "generate_back"
              );
              break;
            }
            if (actionId === "buy_credits_modify") {
              openBuyCredits("modify");
              break;
            }
            if (actionId === "continue_to_back_design") {
              updateFormData({
                frontApproved: true,
                generateBack: true,
              });
              await sendPresetAction(preset, source);
              break;
            }
            if (actionId === "generate_dress_front") {
              await runGeneratePreset(preset, source);
              break;
            }
            if (actionId === "generate_dress_back") {
              await runGeneratePreset(preset, source);
              break;
            }
            if (actionId === "submit_quote") {
              // Quote submission is deterministic page behavior, not a design
              // conversation turn. Execute the reviewed contract directly so
              // a capability mismatch can never reinterpret this click as the
              // typed phrase "Get free quote" and stage an identical widget.
              if (quoteSelection?.quoteScope) {
                await handleCreateTicket(quoteSelection.designSpec, {
                  quoteScope: quoteSelection.quoteScope,
                });
              } else {
                await handleCreateTicket(quoteSelection?.designSpec);
              }
              break;
            }
            await sendPresetAction(preset, source);
          } catch (error) {
            // Error already logged and user message added in specific handlers
            throw error;
          }
          break;
        }
        case "modify": {
          try {
            updateFormDataFromPreset(preset);
            setViewForModifyAction(preset.actionId);
            if (
              (preset.actionId === "modify_dress_front" ||
                preset.actionId === "modify_dress_back") &&
              preset.actionData?.applyChanges
            ) {
              await sendPresetAction(preset, source);
              break;
            }
            await sendPresetAction(preset, source);
          } catch (error) {
            logger.error("Failed to process modify preset", {
              component: "CreateAgentChat",
              metadata: {
                error: error instanceof Error ? error.message : String(error),
                presetId: preset.actionId,
              },
            });
            addMessage({
              role: "assistant",
              content: "We couldn't apply your changes. Please try again.",
            });
          }
          break;
        }
        default:
          try {
            await sendPresetAction(preset, source);
          } catch (error) {
            logger.error("Failed to process preset", {
              component: "CreateAgentChat",
              metadata: {
                error: error instanceof Error ? error.message : String(error),
                presetId: preset.actionId,
              },
            });
            addMessage({
              role: "assistant",
              content: "We couldn't save your selection. Please try again.",
            });
          }
      }
    } finally {
      // Clear processing state after a short delay to allow UI to update
      setTimeout(() => {
        setProcessingPresetId(null);
      }, 300);
    }
  };

  return {
    authLoading,
    processingPresetId,
    selectedPresetLabelsByMessageId,
    selectedChoicesByMessageId,
    setSelectedPresetLabel,
    clearSelectedPresetLabel,
    handlePresetClick,
    handlePresetSelect,
    handleSelectedPresetGenerate,
    handleSelectedPresetChatMore,
    handleRestoredQuote,
    handleRestoredPresetGenerate,
    handleCommittedPresetApply,
  };
}
